import React from 'react';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Icon from '@mui/material/Icon';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import useMediaQuery from '@mui/material/useMediaQuery';
import { alpha, useTheme as useMuiTheme } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDInput from 'components/MDInput';
import MDTypography from 'components/MDTypography';

import { membersApi } from 'sectors/pilates/members/api/membersApi';
import { MemberDetailModal } from 'sectors/pilates/members/modal/MemberDetailModal/MemberDetailModal';
import { reportsApi } from '../api/reportsApi';
import { MemberAnalysisModal, MemberAttendanceFlowModal } from './MemberReportModals';
import { memberName, moneyTry, statusIcon, statusLabel } from './memberReportUtils';

function Surface({ children, sx = {} }) {
  const theme = useMuiTheme();
  return (
    <MDBox
      sx={{
        borderRadius: '22px',
        border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
        background: alpha(theme.palette.background.paper, 0.96),
        ...sx,
      }}
    >
      {children}
    </MDBox>
  );
}

function MetricPill({ label, value, tone = 'info' }) {
  const theme = useMuiTheme();
  const color =
    tone === 'warning'
      ? theme.palette.warning.main
      : tone === 'success'
      ? theme.palette.success.main
      : theme.palette.info.main;

  return (
    <MDBox
      sx={{
        px: 1.1,
        py: 0.8,
        borderRadius: '16px',
        border: `1px solid ${alpha(color, 0.2)}`,
        background: alpha(color, 0.08),
        display: 'grid',
        gap: 0.15,
      }}
    >
      <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
        {label}
      </MDTypography>
      <MDTypography variant="button" fontWeight="bold">
        {value}
      </MDTypography>
    </MDBox>
  );
}

function AttendanceDot({ item }) {
  const theme = useMuiTheme();
  const upper = String(item?.attendanceStatus || '').toUpperCase();
  const attended = upper === 'ATTENDED';
  const negative = ['NO_SHOW', 'BURNED', 'CANCELLED'].includes(upper);
  const color = attended
    ? theme.palette.success.main
    : negative
    ? theme.palette.error.main
    : theme.palette.info.main;

  return (
    <Tooltip
      title={`${item?.dayISO || '—'} • ${item?.trainerName || '—'} • ${statusLabel(
        item?.attendanceStatus
      )}`}
    >
      <MDBox
        sx={{
          minWidth: 54,
          px: 0.55,
          py: 0.38,
          borderRadius: '12px',
          border: `1px solid ${alpha(color, 0.22)}`,
          background: alpha(color, 0.08),
          display: 'grid',
          gap: 0.1,
        }}
      >
        <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={0.4}>
          <MDTypography variant="caption" fontWeight="bold" sx={{ lineHeight: 1 }}>
            {String(item?.dayISO || '').slice(5)}
          </MDTypography>
          <Icon sx={{ fontSize: 13, color }}>{statusIcon(item?.attendanceStatus)}</Icon>
        </MDBox>
        <MDTypography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1 }} noWrap>
          {(item?.trainerName || '—').split(' ')[0]}
        </MDTypography>
      </MDBox>
    </Tooltip>
  );
}

function PackageLane({ pkg }) {
  const theme = useMuiTheme();
  const paid = String(pkg?.paymentBadge || '').toLowerCase() === 'ödendi';

  if (!pkg) {
    return (
      <MDBox
        sx={{
          minHeight: 68,
          px: 0.8,
          py: 0.7,
          borderRadius: '16px',
          border: `1px dashed ${alpha(theme.palette.text.primary, 0.16)}`,
          display: 'grid',
          placeItems: 'center',
        }}
      >
        <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
          Paket yok
        </MDTypography>
      </MDBox>
    );
  }

  return (
    <MDBox
      sx={{
        minHeight: 68,
        px: 0.8,
        py: 0.7,
        borderRadius: '16px',
        border: `1px solid ${alpha(theme.palette.text.primary, 0.07)}`,
        background: alpha(theme.palette.background.default, 0.45),
        display: 'grid',
        gap: 0.45,
      }}
    >
      <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={0.8}>
        <MDBox sx={{ minWidth: 0 }}>
          <MDTypography variant="button" fontWeight="bold" noWrap>
            {pkg.planName}
          </MDTypography>
          <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
            {pkg.usedCount || 0}
            {pkg.totalCount ? ` / ${pkg.totalCount}` : ''} kullanım
          </MDTypography>
        </MDBox>
        <Chip
          size="small"
          label={pkg?.paymentBadge || (paid ? 'Ödendi' : 'Borçlu')}
          color={paid ? 'success' : 'warning'}
          variant="outlined"
          sx={{
            height: 20,
            borderRadius: '999px',
            '& .MuiChip-label': { px: 0.7, fontSize: 10, fontWeight: 800 },
          }}
        />
      </MDBox>

      <MDBox sx={{ display: 'flex', gap: 0.32, overflowX: 'auto', pb: 0.1 }}>
        {(pkg.items || []).map((item) => (
          <AttendanceDot key={`${pkg.memberPlanId}-${item.sessionParticipantId}`} item={item} />
        ))}
      </MDBox>
    </MDBox>
  );
}

function MemberQuickActions({ open, onClose, row, onAction, busy }) {
  const theme = useMuiTheme();

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogContent sx={{ p: 1 }}>
        <Surface sx={{ p: 1 }}>
          <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={1} mb={1}>
            <MDBox>
              <MDTypography fontWeight="bold">{memberName(row)}</MDTypography>
              <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
                Hızlı işlemler
              </MDTypography>
            </MDBox>
            <IconButton onClick={onClose} disabled={busy}>
              <Icon>close</Icon>
            </IconButton>
          </MDBox>

          <MDBox
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 0.8,
            }}
          >
            {[
              ['payments', 'payments', 'Ödemeler', 'Tahsilat geçmişi'],
              ['attendance', 'fact_check', 'Yoklama', 'Paket akışı'],
              ['detail', 'person', 'Detay', 'Üye kartı'],
              ['analysis', 'auto_graph', 'Analiz', 'Hızlı özet'],
            ].map(([key, icon, label, caption]) => (
              <MDBox
                key={key}
                onClick={() => !busy && onAction(key, row)}
                sx={{
                  p: 0.9,
                  borderRadius: '16px',
                  border: `1px solid ${alpha(theme.palette.info.main, 0.14)}`,
                  background: `linear-gradient(180deg, ${alpha(
                    theme.palette.info.main,
                    0.08
                  )}, ${alpha(theme.palette.background.paper, 0.96)})`,
                  cursor: busy ? 'default' : 'pointer',
                  opacity: busy ? 0.65 : 1,
                  display: 'grid',
                  gap: 0.45,
                }}
              >
                <MDBox
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '12px',
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: alpha(theme.palette.info.main, 0.12),
                  }}
                >
                  {busy ? (
                    <CircularProgress size={14} />
                  ) : (
                    <Icon sx={{ fontSize: 18 }}>{icon}</Icon>
                  )}
                </MDBox>
                <MDTypography variant="button" fontWeight="bold">
                  {label}
                </MDTypography>
                <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
                  {caption}
                </MDTypography>
              </MDBox>
            ))}
          </MDBox>
        </Surface>
      </DialogContent>
    </Dialog>
  );
}

function MemberRow({ row, onOpenAction, busy }) {
  const theme = useMuiTheme();
  const [actionsOpen, setActionsOpen] = React.useState(false);

  return (
    <>
      <MDBox
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '210px minmax(0,1fr) minmax(0,1fr) 54px' },
          gap: 0.65,
          alignItems: 'center',
          p: 0.82,
          borderRadius: '16px',
          border: `1px solid ${alpha(theme.palette.text.primary, 0.06)}`,
          background: alpha(theme.palette.background.paper, 0.86),
          transition: 'transform 140ms ease, box-shadow 140ms ease',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: `0 10px 24px ${alpha(theme.palette.common.black, 0.05)}`,
          },
        }}
      >
        <MDBox sx={{ minWidth: 0 }}>
          <MDTypography
            variant="button"
            fontWeight="bold"
            sx={{ fontSize: '0.94rem', cursor: 'pointer' }}
            onClick={() => onOpenAction('detail', row)}
          >
            {memberName(row)}
          </MDTypography>
          <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
            {row.phone || 'Telefon yok'} • Borç {moneyTry(row.debtTry || 0)}
          </MDTypography>
        </MDBox>

        <PackageLane pkg={row.packages?.[0]} />
        <PackageLane pkg={row.packages?.[1]} />

        <MDBox sx={{ display: 'flex', justifyContent: { xs: 'flex-end', lg: 'center' } }}>
          <IconButton
            onClick={() => setActionsOpen(true)}
            disabled={busy}
            sx={{
              width: 38,
              height: 38,
              borderRadius: '14px',
              border: `1px solid ${alpha(theme.palette.info.main, 0.16)}`,
              background: alpha(theme.palette.info.main, 0.08),
            }}
          >
            {busy ? <CircularProgress size={15} /> : <Icon>more_horiz</Icon>}
          </IconButton>
        </MDBox>
      </MDBox>

      <MemberQuickActions
        open={actionsOpen}
        onClose={() => setActionsOpen(false)}
        row={row}
        busy={busy}
        onAction={(type, payload) => {
          setActionsOpen(false);
          onOpenAction(type, payload);
        }}
      />
    </>
  );
}

export function MemberReportsTab() {
  const theme = useMuiTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const [q, setQ] = React.useState('');
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [selectedMember, setSelectedMember] = React.useState(null);
  const [selectedPackages, setSelectedPackages] = React.useState([]);
  const [detailTab, setDetailTab] = React.useState(0);
  const [detailOpen, setDetailOpen] = React.useState(false);
  const [attendanceOpen, setAttendanceOpen] = React.useState(false);
  const [analysisOpen, setAnalysisOpen] = React.useState(false);
  const [actionBusyId, setActionBusyId] = React.useState(null);

  React.useEffect(() => {
    let alive = true;
    setLoading(true);

    const timer = setTimeout(() => {
      reportsApi
        .memberBoard({ q, limit: 18 })
        .then((res) => {
          if (!alive) return;
          setRows(Array.isArray(res) ? res : []);
        })
        .catch(() => {
          if (!alive) return;
          setRows([]);
        })
        .finally(() => {
          if (alive) setLoading(false);
        });
    }, 180);

    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [q]);

  const totalDebt = rows.reduce((sum, row) => sum + Number(row?.debtTry || 0), 0);
  const packageCount = rows.reduce((sum, row) => sum + Number(row?.packages?.length || 0), 0);

  const openDetailModal = async (row, tabIndex) => {
    if (!row?.id) return;
    try {
      setActionBusyId(row.id);
      const detail = await membersApi.detail(row.id);
      setSelectedMember({ ...row, ...detail });
      setSelectedPackages(row.packages || []);
      setDetailTab(tabIndex);
      setDetailOpen(true);
    } finally {
      setActionBusyId(null);
    }
  };

  const onOpenAction = async (type, row) => {
    if (type === 'attendance') {
      setSelectedMember(row);
      setSelectedPackages(row.packages || []);
      setAttendanceOpen(true);
      return;
    }
    if (type === 'analysis') {
      setSelectedMember(row);
      setSelectedPackages(row.packages || []);
      setAnalysisOpen(true);
      return;
    }
    await openDetailModal(row, type === 'payments' ? 1 : 0);
  };

  return (
    <MDBox sx={{ display: 'grid', gap: 1.15 }}>
      <Card
        sx={{
          p: { xs: 1.15, md: 1.35 },
          borderRadius: '24px',
          border: 'none',
          boxShadow: 'none',
          background: `linear-gradient(180deg, ${alpha(theme.palette.info.main, 0.07)}, ${alpha(
            theme.palette.background.paper,
            0.98
          )})`,
        }}
      >
        <MDBox
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', xl: 'minmax(0,1fr) 360px' },
            gap: 1,
            alignItems: 'center',
          }}
        >
          <MDBox>
            <MDTypography variant="h5" fontWeight="bold">
              Üye Seans Listesi
            </MDTypography>
            <MDTypography variant="button" sx={{ color: 'text.secondary' }}>
              Hangi üyede hangi paket var, hangi seansa katıldı, hangi akış bekliyor tek bakışta
              gör.
            </MDTypography>
          </MDBox>

          <MDInput
            fullWidth
            label="Üye ara"
            placeholder="İsim soyisim..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </MDBox>

        <MDBox
          sx={{
            mt: 1,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
            gap: 0.8,
          }}
        >
          <MetricPill label="Üye" value={rows.length} />
          <MetricPill label="Paket" value={packageCount} tone="success" />
          <MetricPill label="Açık Borç" value={moneyTry(totalDebt)} tone="warning" />
          <MetricPill label="Görünüm" value="Kompakt Liste" />
        </MDBox>
      </Card>

      <Surface sx={{ p: 0.9 }}>
        {!isMobile ? (
          <MDBox
            sx={{
              display: 'grid',
              gridTemplateColumns: '220px minmax(0,1fr) minmax(0,1fr) 54px',
              gap: 0.8,
              px: 0.7,
              pb: 0.7,
              alignItems: 'center',
            }}
          >
            <MDTypography variant="caption" fontWeight="bold" sx={{ color: 'text.secondary' }}>
              Üye
            </MDTypography>
            <MDTypography variant="caption" fontWeight="bold" sx={{ color: 'text.secondary' }}>
              Kapsayan Paket
            </MDTypography>
            <MDTypography variant="caption" fontWeight="bold" sx={{ color: 'text.secondary' }}>
              İkinci Paket
            </MDTypography>
            <MDTypography variant="caption" fontWeight="bold" sx={{ color: 'text.secondary' }}>
              İşlem
            </MDTypography>
          </MDBox>
        ) : null}

        <MDBox sx={{ display: 'grid', gap: 0.7 }}>
          {loading ? (
            <MDBox sx={{ minHeight: 180, display: 'grid', placeItems: 'center' }}>
              <CircularProgress size={28} />
            </MDBox>
          ) : null}

          {!loading && rows.length === 0 ? (
            <MDBox sx={{ p: 2, display: 'grid', placeItems: 'center' }}>
              <MDTypography variant="button" sx={{ color: 'text.secondary' }}>
                Bu filtrede üye bulunamadı.
              </MDTypography>
            </MDBox>
          ) : null}

          {!loading &&
            rows.map((row) => (
              <MemberRow
                key={row.id}
                row={row}
                onOpenAction={onOpenAction}
                busy={actionBusyId === row.id}
              />
            ))}
        </MDBox>
      </Surface>

      <MemberDetailModal
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        member={selectedMember}
        initialTab={detailTab}
      />

      <MemberAttendanceFlowModal
        open={attendanceOpen}
        onClose={() => setAttendanceOpen(false)}
        member={selectedMember}
        groups={selectedPackages}
      />

      <MemberAnalysisModal
        open={analysisOpen}
        onClose={() => setAnalysisOpen(false)}
        member={selectedMember}
        groups={selectedPackages}
      />
    </MDBox>
  );
}
